<template>
  <div>
    <header class="tw-flex tw-ml-4 tw-mr-6">
      <SnowChartHeader
        :amount-cumulative="snowSummary[0].amountCumulative"
        :amount-cumulative-text-style="snowSummary[0].amountCumulativeTextStyle"
        :can-show-amount-cumulative="isAllAccess"
        class="tw-w-1/2 tw-whitespace-nowrap"
        :label="snowSummary[0].label"
      />
      <SnowChartHeader
        :amount-cumulative="snowSummary[1].amountCumulative"
        :amount-cumulative-text-style="snowSummary[1].amountCumulativeTextStyle"
        :can-show-amount-cumulative="isAllAccess"
        class="tw-w-1/2 tw-whitespace-nowrap"
        :label="snowSummary[1].label"
        :show-left-border="false"
      />
    </header>
    <div :class="chartContainerClass">
      <canvas ref="ctx" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import {
  dataNotAvailable,
  formatSnow,
  isMetric,
  isScreenLg,
  isScreenSm,
  safeRound,
} from '@@/utils/CommonUtils';
import {
  canBeResponsive,
  chartColors,
  getBorderRadius,
  getLockedHoursPlugin,
} from '@@/utils/ChartUtils';
import { useUserStore } from '@@/stores/User';
import SnowObservationAlertMixin from '@@/components/WeatherStations/SnowObservationAlertMixin';

export default {
  name: 'WeatherStationSnowChart',

  mixins: [SnowObservationAlertMixin],

  props: {
    hasCam: {
      type: Boolean,
      default: false,
    },
    range: {
      type: Object,
      default: null,
    },
    weatherStation: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapState(useUserStore, ['isAllAccess']),

    ...mapState(useUserStore, {
      theme: (state) => state.computedTheme,
      units: (state) => state.preferences.units,
    }),

    chartContainerClass() {
      return [
        'tw-h-20 lg:tw-h-28',
        this.$style.chartContainer,
        this.hasCam ? this.$style.chartContainerWithCam : null,
      ];
    },

    isMetric() {
      return isMetric(this.units);
    },

    snowSummary() {
      const [first, last] = this.weatherStation?.observation_snow_summary || [];

      const getTextStyle = (period) => {
        let color = 'var(--text-darkest)';

        if (this.hasMix(period)) {
          color = this.snowObservationAlertMixLevel.color_foreground;
        }
        else if (this.hasPowder(period)) {
          color = this.snowObservationAlertPowderLevel.color_foreground;
        }
        else if (this.hasSnow(period)) {
          color = this.snowObservationAlertSnowLevel.color_foreground;
        }

        return { color };
      };

      const firstPrecipSnow = safeRound(first?.precip_snow);
      const lastPrecipSnow = safeRound(last?.precip_snow);

      return [{
        amountCumulative: this.isMetric
          ? firstPrecipSnow
          : formatSnow(firstPrecipSnow, this.units, true),
        amountCumulativeTextStyle: getTextStyle(first),
        label: first?.display_at_local_label || dataNotAvailable,
      }, {
        amountCumulative: this.isMetric
          ? lastPrecipSnow
          : formatSnow(lastPrecipSnow, this.units, true),
        amountCumulativeTextStyle: getTextStyle(last),
        label: last?.display_at_local_label || dataNotAvailable,
      }];
    },
  },

  watch: {
    weatherStation() {
      this.renderChart();
    },

    theme() {
      this.renderChart();
    },
  },

  mounted() {
    this.renderChart();
  },

  methods: {
    getConfig() {
      return {
        data: {
          datasets: [],
          labels: this
            .weatherStation
            .observation_hourly
            .map((hour) => hour.display_at_local_label),
        },
        options: {
          datasets: {
            bar: {
              barPercentage: 0.85,
              categoryPercentage: 1,
            },
          },
          layout: {
            padding: {
              top: 16,
            },
          },
          maintainAspectRatio: false,
          plugins: {
            datalabels: {
              align: 'end',
              anchor: 'end',
              color: chartColors.textDarkest(this.theme),
              font: {
                size: isScreenLg() ? 16 : 11,
                weight: '400',
              },
              formatter: (value, context) => {
                if (!this.isAllAccess) {
                  return '';
                }

                const { dataIndex } = context;
                const hour = this.weatherStation.observation_hourly[dataIndex];

                if (this.hasMissingData(hour)) {
                  return 'X';
                }

                return '';
              },
              offset: -3,
              textAlign: 'center',
            },
            legend: {
              display: false,
            },
            title: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
          },
          responsive: canBeResponsive(),
          scales: {},
        },
        type: 'bar',
      };
    },

    /**
     * @todo Add suspect hourly data set
     */
    getDataSets() {
      /* eslint camelcase: off */
      const mixData = [];
      const noPrecipData = [];
      const powderData = [];
      const snowData = [];

      this.weatherStation.observation_hourly.forEach((hour, index) => {
        /* eslint no-console: off */

        // TODO: Need to display hours with suspect report observations in the chart!
        if (this.hasSuspectObservation(hour)) {
          console.log(`LiveSnowChart: Hour ${index} has a suspect observation alert!`);
        }

        const mix = this.hasMix(hour) ? hour.precip_snow : undefined;
        const noPrecip = this.hasNoPrecip(hour) ? hour.precip_snow : undefined;
        const powder = this.hasPowder(hour) ? hour.precip_snow : undefined;
        const snow = this.hasSnow(hour) ? hour.precip_snow : undefined;

        mixData.push(mix);
        noPrecipData.push(noPrecip);
        powderData.push(powder);
        snowData.push(snow);
      });

      const mixBackgroundColor = this.snowObservationAlertMixLevel.color_primary;
      const noPrecipBackgroundColor = chartColors.textRegular(this.theme);
      const powderBackgroundColor = this.snowObservationAlertPowderLevel.color_primary;
      const snowBackgroundColor = this.snowObservationAlertSnowLevel.color_primary;

      const borderRadius = getBorderRadius();
      const minBarLength = 4;

      return [{
        backgroundColor: mixBackgroundColor,
        borderRadius,
        data: mixData,
        hoverBackgroundColor: mixBackgroundColor,
        minBarLength,
      }, {
        backgroundColor: noPrecipBackgroundColor,
        borderRadius,
        data: noPrecipData,
        hoverBackgroundColor: noPrecipBackgroundColor,
        minBarLength,
      }, {
        backgroundColor: powderBackgroundColor,
        borderRadius,
        data: powderData,
        hoverBackgroundColor: powderBackgroundColor,
        minBarLength,
      }, {
        backgroundColor: snowBackgroundColor,
        borderRadius,
        data: snowData,
        hoverBackgroundColor: snowBackgroundColor,
        minBarLength,
      }];
    },

    getScales() {
      // eslint-disable-next-line
      const self = this;
      let maxHourlyPrecip;

      // Use the range if specified so that when showing multiple weather stations on the the
      // Location Weather Stations page they can all use the same y-axis scale.

      if (this.range?.max) {
        maxHourlyPrecip = this.range.max;
      }
      else {
        const precips = this.weatherStation
          .observation_hourly
          .map((hour) => (this.hasMissingData(hour) ? 0 : hour.precip_snow));
        maxHourlyPrecip = Math.max(...precips);
      }

      let max;
      let stepSize;

      if (this.isMetric) {
        // Default to 5cm if less than 5cm per hour of precip snow has been reported
        max = Math.ceil(maxHourlyPrecip > 5 ? maxHourlyPrecip : 5);
        stepSize = 1;
      }
      else {
        // Default to 2" if less than 2" per hour of precip snow has been reported
        max = Math.ceil(maxHourlyPrecip > 2 ? maxHourlyPrecip : 2);
        stepSize = max > 2 ? 1 : 0.5;
      }

      const getHourDisplayAtLocalLabel = (index) => this
        .weatherStation
        .observation_hourly[index]
        .display_at_local_label;

      const getYAxisCommonParams = (isLeft = true) => ({
        afterFit(scaleInstance) {
          /* eslint no-param-reassign: off */
          // Set y-axis width to 20px
          // SEE: https://github.com/chartjs/Chart.js/issues/2626
          scaleInstance.width = 20;
        },
        display: true,
        max,
        min: 0,
        stacked: true,
        ticks: {
          autoSkip: false,
          beginAtZero: true,
          callback(value, index) {
            // Never label the 0 y-axis tick
            // When using metric units and max > 5cm then label every third grid line (3cm, 6cm)
            // When using metric units and max <= 5cm then label every even grid line (2cm, 4cm)
            // When using imperial units and max > 2" then label odd grid lines (1", 3", etc)
            // When using imperial units and max <= 2" then label even grid lines (1", 2")

            if (value === 0) {
              // Don't label 0" y-axis tick
              return '';
            }

            if ((self.isMetric && max > 5 && index % 3 === 0)
              || (self.isMetric && max <= 5 && index % 2 === 0)
              || (!self.isMetric && max > 2 && index % 2 !== 0)
              || (!self.isMetric && max <= 2 && index % 2 === 0)) {
              return formatSnow(value, self.units, true);
            }

            return '';
          },
          color: chartColors.textDark(this.theme),
          count: max,
          font: {
            size: isScreenLg() ? 11 : 10,
          },
          padding: isLeft ? -1 : -9,
          precision: 1,
          stepSize,
        },
        type: 'linear',
      });

      return {
        x: {
          grid: {
            display: false,
          },
          stacked: true,
          ticks: {
            autoSkip: false,
            color: chartColors.textDark(this.theme),
            callback(value, index) {
              if (isScreenSm()) {
                return (index + 1) % 3 === 0 ? getHourDisplayAtLocalLabel(index) : '';
              }

              return getHourDisplayAtLocalLabel(index);
            },
            font: {
              size: 11,
            },
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          border: {
            dash({ index }) {
              return index === 0 ? false : [2, 2];
            },
            display: false,
          },
          grid: {
            color: chartColors.borderColor(this.theme),
            drawTicks: false,
            lineWidth: 1,
          },
          position: 'left',
          ...getYAxisCommonParams(true),
        },
        yAxes: {
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
          position: 'right',
          ...getYAxisCommonParams(false),
        },
      };
    },

    hasMissingData(period = {}) {
      return period?.precip_snow === null;
    },

    renderChart() {
      /* eslint no-unused-vars: off */

      if (this.barChart) {
        // TODO: May need to remove the canvas and recreate it if any strange issues are noticed
        // when re-rendering the chart when units change
        // SEE: https://stackoverflow.com/questions/40056555/destroy-chart-js-bar-graph-to-redraw-other-graph-in-same-canvas
        this.barChart.destroy();
      }

      const config = this.getConfig();
      config.data.datasets = this.getDataSets();
      config.options.scales = this.getScales();

      if (!this.isAllAccess) {
        const checkCanShowHour = (hour) => {
          if (this.isAllAccess) {
            return true;
          }

          if (isScreenLg()) {
            return false;
          }

          // Every third hour can be shown, which means the lock will be drawn for every third,
          // hour on mobile devices.
          return (hour + 1) % 3 !== 0;
        };

        config.plugins = [
          getLockedHoursPlugin(this.isAllAccess, checkCanShowHour),
        ];
      }

      this.barChart = new this.$chart(this.$refs.ctx, config);
    },
  },
};
</script>

<style module>
.chartContainer {
  --margin-x: 1.4rem;
}

@media (min-width: 576px) {
  .chartContainer {
    --margin-x: 1.6rem;
  }
}

@media (min-width: 768px) {
  .chartContainer {
    --margin-x: 1.9rem;
  }

  .chartContainerWithCam {
    --margin-x: 1.7rem;
  }
}

@media (min-width: 992px) {
  .chartContainer {
    --margin-x: 2.2rem;
  }

  .chartContainerWithCam {
    --margin-x: 2rem;
  }
}

@media (min-width: 1200px) {
  .chartContainer {
    --margin-x: 2.4rem;
  }

  .chartContainerWithCam {
    --margin-x: 2.1rem;
  }
}

.chartContainer::after {
  color: var(--saturated-red);
  content: '•';
  display: block;
  height: 0.75rem;
  line-height: 1;
  margin-left: var(--margin-x);
  margin-right: var(--margin-x);
  margin-top: -0.5rem;
  text-align: right;
}
</style>
